/**
 * Scale Utilities - Shared scale calculation logic for construction documents
 */

/**
 * Calculate scale to fit content within canvas bounds with padding
 * @param {number} contentWidth - Width of content in meters
 * @param {number} contentHeight - Height of content in meters
 * @param {number} canvasWidth - Canvas width in pixels
 * @param {number} canvasHeight - Canvas height in pixels
 * @param {number} padding - Padding in pixels (default 80)
 * @returns {number} Scale factor (pixels per meter)
 */
export const calculateFitScale = (
  contentWidth,
  contentHeight,
  canvasWidth,
  canvasHeight,
  padding = 80
) => {
  const availableWidth = canvasWidth - padding * 2;
  const availableHeight = canvasHeight - padding * 2;

  const scaleX = availableWidth / contentWidth;
  const scaleY = availableHeight / contentHeight;

  return Math.min(scaleX, scaleY);
};

/**
 * Find the nearest "nice" round number for scale bar display
 * @param {number} distance - Distance to round
 * @param {number[]} roundNumbers - Array of acceptable round numbers
 * @returns {number} Nearest round number
 */
export const getNiceRoundNumber = (
  distance,
  roundNumbers = [1, 2, 5, 10, 20, 50, 100, 200, 500]
) => {
  let bestMatch = roundNumbers[0];
  let minDiff = Math.abs(distance - bestMatch);

  for (const num of roundNumbers) {
    const diff = Math.abs(distance - num);
    if (diff < minDiff) {
      minDiff = diff;
      bestMatch = num;
    }
  }

  return bestMatch;
};

/**
 * Calculate scale bar metrics for display
 * @param {number} scale - Current scale factor (pixels per meter)
 * @param {number} referenceLength - Reference length in pixels (default 100)
 * @returns {{scaleBarFeet: number, scaleBarLength: number, scaleBarMeters: number}}
 */
export const getScaleBarMetrics = (scale, referenceLength = 100) => {
  // Calculate what distance the reference length represents
  const distanceInMeters = referenceLength / scale;
  const distanceInFeet = distanceInMeters * 3.28084;

  // Find a nice round number in feet
  const scaleBarFeet = getNiceRoundNumber(distanceInFeet);

  // Calculate back to meters and pixels
  const scaleBarMeters = scaleBarFeet / 3.28084;
  const scaleBarLength = scaleBarMeters * scale;

  return {
    scaleBarFeet,
    scaleBarMeters,
    scaleBarLength,
  };
};

/**
 * Convert meters to feet
 * @param {number} meters - Distance in meters
 * @returns {number} Distance in feet
 */
export const metersToFeet = (meters) => {
  return meters * 3.28084;
};

/**
 * Convert feet to meters
 * @param {number} feet - Distance in feet
 * @returns {number} Distance in meters
 */
export const feetToMeters = (feet) => {
  return feet / 3.28084;
};

/**
 * Format distance for display
 * @param {number} distance - Distance in feet
 * @param {number} decimals - Number of decimal places (default 1)
 * @returns {string} Formatted string with feet symbol
 */
export const formatFeet = (distance, decimals = 1) => {
  return `${distance.toFixed(decimals)}'`;
};

/**
 * Calculate area from polygon points
 * @param {Array<[number, number]>} polygon - Array of [x, z] points
 * @returns {number} Area in square meters
 */
export const calculatePolygonArea = (polygon) => {
  let area = 0;
  const n = polygon.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += polygon[i][0] * polygon[j][1];
    area -= polygon[j][0] * polygon[i][1];
  }

  return Math.abs(area) / 2;
};

/**
 * Convert area from square meters to square feet
 * @param {number} squareMeters - Area in square meters
 * @returns {number} Area in square feet
 */
export const squareMetersToSquareFeet = (squareMeters) => {
  return squareMeters * 10.764;
};
